import { withNamespaces } from "react-i18next";
import {
    Badge,
    Card,
    CardBody,
    Col,
    Row,
    UncontrolledTooltip
} from "reactstrap";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import { APPROVAL_STATUS_MAP } from "src/modules/data-protection/constants";
import { getRequirementTitle } from "src/modules/data-protection/pages/admin/software/details/tabs-content/information/components/required-approvals/helpers";

/**
 * The payload also carries statuses outside the shared map (for example
 * `missing_assignee`), so unknown values keep their own label instead of
 * being reported as Pending.
 */
const getStatusConfig = (status) => {
    if (APPROVAL_STATUS_MAP[status]) {
        return APPROVAL_STATUS_MAP[status];
    }

    return {
        label: String(status || "")
            .replace(/_/g, " ")
            .replace(/^./, (character) => character.toUpperCase()),
        color: "secondary"
    };
};

const ApprovalCard = ({ t, requirement, infoTargetId }) => {
    const dateUtils = new DateUtils();
    const config = getStatusConfig(requirement.status);
    const approvedBy =
        requirement.approved_by?.full_name || requirement.approvedBy?.full_name;
    const approvedAt = requirement.approved_at || requirement.approvedAt;
    const assignee =
        requirement.assignee?.full_name || requirement.assignee?.fullName;

    const details = [
        approvedBy && `${t("Approved by")}: ${approvedBy}`,
        approvedAt &&
            `${t("Approved at")}: ${dateUtils.convertDateToDate(
                approvedAt,
                INTERNATIONAL_DATE_FORMAT
            )}`,
        assignee && `${t("Assignee")}: ${assignee}`
    ].filter(Boolean);

    return (
        <Card className="overview-approval-card h-100 mb-0">
            <CardBody className="d-flex align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-2">
                    <strong>{t(getRequirementTitle(requirement))}</strong>
                    <i
                        className="ri-information-line text-muted"
                        id={infoTargetId}
                    />
                    <UncontrolledTooltip target={infoTargetId}>
                        {details.length
                            ? details.join(" · ")
                            : t("Awaiting approval.")}
                    </UncontrolledTooltip>
                </div>
                <Badge
                    color={config.color}
                    className={`bg-${config.color}`}
                >
                    {t(config.label)}
                </Badge>
            </CardBody>
        </Card>
    );
};

const OverviewApprovals = ({ t, softwareData, approvals = {} }) => {
    const assetId = softwareData?.id;
    const requirements = approvals.requirements || [];

    return (
        <div className="mb-4">
            <h6 className="overview-section-title mb-3">
                {t("Compliance Approvals")}
            </h6>
            {requirements.length ? (
                <Row className="g-3">
                    {requirements.map((requirement, index) => {
                        const key =
                            requirement.id ?? requirement.type ?? index;

                        return (
                            <Col xs="12" md="6" lg={requirements.length > 3 ? "3" : "4"} key={key}>
                                <ApprovalCard
                                    t={t}
                                    requirement={requirement}
                                    infoTargetId={`overview-approval-info-${assetId}-${key}`}
                                />
                            </Col>
                        );
                    })}
                </Row>
            ) : (
                <p className="text-muted mb-0">
                    {t("No approvals required yet.")}
                </p>
            )}
        </div>
    );
};

export default withNamespaces()(OverviewApprovals);
